import React, { useState, useEffect } from 'react';
import CertificationList from './components/CertificationList';
import CreateCertification from './components/CreateCertification';
import LearnerProgress from './components/LearnerProgress';
import ExpiryRenewals from './components/ExpiryRenewals';
import UserCertificates from './components/UserCertificates/UserCertificates';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import './CertificationManagement.css';

type TabType = 'list' | 'create' | 'progress' | 'expiry' | 'user-certificates';

interface TabItem {
  id: TabType;
  label: string;
  badge?: number;
}

const CertificationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('list');

  const {
    certifications,
    loading,
    fetchCertifications,
  } = useCertificationManagementStore();

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'list' && certifications.length === 0 && !loading) {
      fetchCertifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const tabs: TabItem[] = [
    { id: 'list', label: 'Certification List' },
    { id: 'create', label: 'Create Certification' },
    { id: 'progress', label: 'Learner Progress' },
    { id: 'expiry', label: 'Expiry & Renewals' },
    { id: 'user-certificates', label: 'User Certificates' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return <CertificationList />;
      case 'create':
        return <CreateCertification />;
      case 'progress':
        return <LearnerProgress />;
      case 'expiry':
        return <ExpiryRenewals />;
      case 'user-certificates':
        return <UserCertificates />;
      default:
        return <CertificationList />;
    }
  };

  return (
    <div className="s-admin-certifi-management">
      <div className="s-admin-certifi-header">
        <div className="s-admin-certifi-breadcrumb">
          <span>Content Management</span>
          <span className="s-admin-certifi-separator">›</span>
          <span>Certification Management</span>
        </div>
        <h1>Certification Management</h1>
        <p>Issue, track, and manage professional certifications</p>
      </div>

      <div className="s-admin-certifi-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`s-admin-certifi-tab ${activeTab === tab.id ? 's-admin-certifi-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.badge && <span className="s-admin-certifi-tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      <div className="s-admin-certifi-content">{renderTabContent()}</div>
    </div>
  );
};

export default CertificationManagement;
